import React, { useState } from "react";
import {
  HiOutlineMail,
  HiOutlineLockClosed,
  HiOutlineUser,
} from "react-icons/hi";
import { useNavigate, Link } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import { register } from "../../utils/api";
import SnackbarAlert from "../common/sanckbar";
import "./register.scss";

const Register: React.FC = () => {
  const navigate = useNavigate();
  const [regData, setRegData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    organization: "",
    status: "inactive",
    status_id: 0,
  });

  const [errorState, setErrorState] = useState({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    organization: "",
  });

  const [isLoading, setIsLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "warning" as "warning" | "error" | "success" | "info",
  });

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setRegData({
      ...regData,
      [name]: value,
    });
  };

  const validateForm = () => {
    const newErrorState = { ...errorState };

    if (!regData.email) newErrorState.email = "Email is required";
    else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(regData.email)) {
      newErrorState.email = "Invalid email address";
    } else newErrorState.email = "";

    if (!regData.first_name) newErrorState.first_name = "First name is required";
    else newErrorState.first_name = "";

    if (!regData.last_name) newErrorState.last_name = "Last name is required";
    else newErrorState.last_name = "";

    if (!regData.password) newErrorState.password = "Password is required";
    else newErrorState.password = "";

    setErrorState(newErrorState);
    return Object.values(newErrorState).every((error) => error === "");
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      setSnackbar({
        open: true,
        message: "Please fix the errors in the form",
        severity: "warning",
      });
      return;
    }

    setIsLoading(true);
    try {
      const response = await register("register", regData);
      if (response) {
        setSnackbar({
          open: true,
          message: response.message || "Registration successful",
          severity: "success",
        });
        setTimeout(() => navigate("/login"), 2000);
      } else {
        setSnackbar({
          open: true,
          message: "Registration failed",
          severity: "warning",
        });
      }
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || "Registration failed",
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const closeSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <div className="register-page">
      <SnackbarAlert
        open={snackbar.open}
        closeSnackbar={closeSnackbar}
        message={snackbar.message}
        severity={snackbar.severity}
      />
      <div className="left-section">
        <div className="logo">
          <span className="logo-icon">DC</span>
        </div>
        <h1>Delnie Cloud</h1>
        <p>
          Efficiently manage master data with centralized control, role-level
          security, and seamless API creation. Integrate with MongoDB Charts for
          real-time analytics and customized insights, tailored to your business
          needs.
        </p>
      </div>
      <div className="right-section">
        <div className="portal-info">
          <span className="portal-icon">🖥️</span>
          <div>
            <p className="portal-title">ACCERON Client Portal</p>
            <p className="portal-subtitle">Powered by Delnie Cloud Admin</p>
          </div>
        </div>
        <h2>Create Account</h2>
        <p className="subtitle">Enter your details to register</p>
        <form onSubmit={handleRegister}>
          <div className="form-group">
            <label htmlFor="first_name">First Name</label>
            <div className="input-wrapper">
              <HiOutlineUser className="input-icon" />
              <input
                type="text"
                id="first_name"
                name="first_name"
                value={regData.first_name}
                onChange={handleChange}
                placeholder="First Name"
                required
                aria-label="First Name"
              />
            </div>
            {errorState.first_name && (
              <span className="error">{errorState.first_name}</span>
            )}
          </div>
          <div className="form-group">
            <label htmlFor="last_name">Last Name</label>
            <div className="input-wrapper">
              <HiOutlineUser className="input-icon" />
              <input
                type="text"
                id="last_name"
                name="last_name"
                value={regData.last_name}
                onChange={handleChange}
                placeholder="Last Name"
                required
                aria-label="Last Name"
              />
            </div>
            {errorState.last_name && (
              <span className="error">{errorState.last_name}</span>
            )}
          </div>
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <div className="input-wrapper">
              <HiOutlineMail className="input-icon" />
              <input
                type="email"
                id="email"
                name="email"
                value={regData.email}
                onChange={handleChange}
                placeholder="Email"
                required
                aria-label="Email address"
              />
            </div>
            {errorState.email && (
              <span className="error">{errorState.email}</span>
            )}
          </div>
          <div className="form-group">
            <label htmlFor="password">Password</label>
            <div className="input-wrapper">
              <HiOutlineLockClosed className="input-icon" />
              <input
                type="password"
                id="password"
                name="password"
                value={regData.password}
                onChange={handleChange}
                placeholder="••••••••"
                required
                aria-label="Password"
              />
            </div>
            {errorState.password && (
              <span className="error">{errorState.password}</span>
            )}
          </div>
          <button type="submit" className="sign-in-button" disabled={isLoading}>
            {isLoading ? (
              <>
                <CircularProgress
                  size={20}
                  style={{ color: "#fff", marginRight: "8px" }}
                />
                Registering...
              </>
            ) : (
              "Get Started"
            )}
          </button>
        </form>
        <p className="create-account">
          Already have an account? <Link to="/login">Login</Link>
        </p>
      </div>
    </div>
  );
};

export default Register;
